import { screenToWorld, type Camera, type Point } from '../camera';
import { anchorPoint, nearestAnchor } from '../connectors';
import { hitTest } from '../hit-test';
import type { EndInput, Intent, KeyInput, PointerInput, SceneReader, Tool } from './tool';

const DRAG_PX = 4;
const HIT_PX = 4;

/** The connector end under world point `p`: attached to the topmost object there (never a connector nor `exclude`), else free. */
export function endAt(reader: SceneReader, p: Point, zoom: number, exclude: ReadonlySet<string> = new Set()): EndInput {
  const objs = reader.objects();
  const id = hitTest(reader.index(), objs, p, HIT_PX, zoom, true, Infinity); // a section's whole inside is a target (P5e)
  const o = id ? objs.get(id) : undefined;
  if (!o || o.type === 'connector' || exclude.has(o.id)) return { objectId: null, anchor: null, x: p.x, y: p.y };
  const anchor = nearestAnchor(o.obb, p, zoom);
  const at = anchorPoint(o.obb, anchor);
  return { objectId: o.id, anchor, x: at.x, y: at.y };
}

/** Drag from an object (or anywhere) to another to connect them; a click alone creates nothing. From an object into the void: `connectOpen`. */
export function createConnectorTool(reader: SceneReader, camera: () => Camera): Tool {
  let from: { screen: Point; end: EndInput } | null = null;
  const end = (e: PointerInput) => {
    const ex = new Set(from?.end.objectId ? [from.end.objectId] : []);
    return endAt(reader, screenToWorld(camera(), e), camera().z, ex);
  };
  const dragged = (e: PointerInput) => !!from && Math.hypot(e.x - from.screen.x, e.y - from.screen.y) >= DRAG_PX;
  return {
    onPointerDown(e) {
      from = { screen: { x: e.x, y: e.y }, end: endAt(reader, screenToWorld(camera(), e), camera().z) };
      return [];
    },
    onPointerMove(e) {
      if (!from || !dragged(e)) return [];
      return [{ kind: 'connect', from: from.end, to: end(e), commit: false }];
    },
    onPointerUp(e) {
      if (!from) return [];
      const to = end(e);
      // From an object into the void: the app offers to add a linked object there (the preview stays).
      const open = from.end.objectId !== null && to.objectId === null;
      const out: Intent[] = !dragged(e) ? [] : open ? [{ kind: 'connectOpen', from: from.end, at: { x: to.x, y: to.y } }] : [{ kind: 'connect', from: from.end, to, commit: true }];
      from = null;
      return out;
    },
    onKey(e: KeyInput) {
      if (e.key !== 'Escape' || !from) return [];
      from = null;
      return [{ kind: 'cancel' }];
    },
    onCancel() {
      const was = from !== null;
      from = null;
      return was ? [{ kind: 'cancel' }] : [];
    },
  };
}
